import { ReactElement, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  AlertContainer,
  AlertMessage,
  AlertTitle,
  CloseIcon,
  CloseIconContainer,
  ContentContainer,
  IconContainer,
  IconSizeContainer,
  MessageLinkContainer,
  StyledLink
} from 'syf-component-library/ui/patterns/AlertBanner/subcomponents'
import severityOptions from 'syf-component-library/ui/patterns/AlertBanner/helpers'
import { FA_TIMES } from 'syf-component-library/const/iconProps'
import { useAtomValue, useSetAtom } from 'jotai'
import { dashboardAnalyticsAtom, featureFlagsFetchedAtom } from 'store/atoms'
import useDisasterBannerStatus from 'hooks/useDisasterBannerStatus'

const CLICK_TRACKING_ATTRIBUTES = {
  'data-analytics': 'clicktrackingevent',
  'data-object': 'link',
  'data-type': 'dashboard'
} as const

export { getBannerSignature } from 'hooks/useDisasterBannerStatus'

const DisasterAlertBanner = (): ReactElement | null => {
  const { isExpected, bannerProps, dismiss } = useDisasterBannerStatus()
  const setDashboardAnalytics = useSetAtom(dashboardAnalyticsAtom)
  const isFeatureFlagsFetched = useAtomValue(featureFlagsFetchedAtom)

  // Only signal once feature flags are calibrated so isExpected is reliable
  useEffect(() => {
    if (!isFeatureFlagsFetched) return

    if (isExpected && bannerProps) {
      setDashboardAnalytics((prev) => ({
        ...prev,
        notificationType: `disasterRecoveryRelief:${bannerProps.alertTitle}:${bannerProps.alertMessage}`,
        bannerResolved: 'true'
      }))
    } else {
      setDashboardAnalytics((prev) => ({ ...prev, bannerResolved: 'true' }))
    }
  }, [isFeatureFlagsFetched, isExpected, bannerProps, setDashboardAnalytics])

  if (!isExpected || !bannerProps) return null

  const { alertSeverity, alertTitle, alertMessage, alertLink, alertLinkText } =
    bannerProps

  return (
    <AlertContainer severity={alertSeverity} data-test="alert-banner">
      <ContentContainer>
        <IconContainer severity={alertSeverity}>
          <IconSizeContainer>
            <FontAwesomeIcon icon={severityOptions[alertSeverity].icon} />
          </IconSizeContainer>
        </IconContainer>
        <MessageLinkContainer>
          <AlertTitle severity={alertSeverity}>{alertTitle}: </AlertTitle>
          <AlertMessage>{alertMessage}</AlertMessage>
          {alertLink && alertLinkText && (
            <StyledLink
              {...CLICK_TRACKING_ATTRIBUTES}
              data-reason={alertLinkText.toLowerCase()}
              href={alertLink}
            >
              {alertLinkText}
            </StyledLink>
          )}
        </MessageLinkContainer>
        <CloseIconContainer>
          <CloseIcon
            {...CLICK_TRACKING_ATTRIBUTES}
            ariaLabel="Close disaster relief banner"
            buttonType="text"
            data-reason="x button"
            onClick={dismiss}
            type="button"
          >
            <FontAwesomeIcon icon={FA_TIMES} />
          </CloseIcon>
        </CloseIconContainer>
      </ContentContainer>
    </AlertContainer>
  )
}

export default DisasterAlertBanner
